import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  firmLogoFillService,
  type FirmLogoFillItem,
  type FirmLogoFillRunState,
} from "../../../api/firmLogoFill.service.js";
import { firmLogoKeys } from "../../../api/profiles/queryKeys.js";
import { formatDateLong } from "../../../utils/dateFormatter.js";
import { FirmLogoCopyDownloads } from "./FirmLogoCopyDownloads.js";
import { FirmLogoCopyFilters } from "./FirmLogoCopyFilters.js";
import { FirmLogoCopyResults, lastPageOf } from "./FirmLogoCopyResults.js";
import { FirmLogoCopyStatus } from "./FirmLogoCopyStatus.js";
import { logoSetIdOf } from "./logoPick.js";
import { isNeedsCheckingReason } from "./outcomeLabels.js";
import { OutcomeSummary } from "./OutcomeSummary.js";
import { locationLabel, websiteLabel } from "./rowLabels.js";
import { useFirmLogoCopyExport } from "./useFirmLogoCopyExport.js";
import { type FilterChanges, useFirmLogoCopyFilters } from "./useFirmLogoCopyFilters.js";
import styles from "./FirmLogoCopy.module.css";

const PAGE_SIZE = 50;

function lastRunText(lastRun: FirmLogoFillRunState): string {
  if (lastRun.state === "never" || lastRun.at === null) {
    return "The copy hasn't been run yet.";
  }

  if (lastRun.state === "applied") {
    return `Logos were copied on ${formatDateLong(lastRun.at)}.`;
  }

  return `The copy was undone on ${formatDateLong(lastRun.at)}.`;
}

export default function FirmLogoCopyPage() {
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const canUse = hasPermission("l500_data.wp_syncer.read");
  const canPickLogos =
    hasFlag("firm-logos-editor") &&
    hasPermission("profiles.read") &&
    hasPermission("profiles.write");

  const { reason, website, company, unresolved, page, updateFilters, replacePage } =
    useFirmLogoCopyFilters();

  const queryKey = JSON.stringify([reason, website, company, unresolved, page]);

  const {
    data: result,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(async () => {
    if (!canUse) {
      return null;
    }

    const response = await firmLogoFillService.list({
      reason,
      website,
      company,
      unresolved,
      page,
      pageSize: PAGE_SIZE,
    });

    return { queryKey, response };
  }, [canUse, reason, website, company, unresolved, page]);

  const data = result?.queryKey === queryKey ? result.response : null;

  const queryClient = useQueryClient();
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [pendingSaveRefreshes, setPendingSaveRefreshes] = useState(0);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [savedAnnouncement, setSavedAnnouncement] = useState<string | null>(null);
  const [refreshAfterSaveError, setRefreshAfterSaveError] = useState<string | null>(null);
  const [focusAfterSave, setFocusAfterSave] = useState<string | null>(null);
  const savedNoticeRef = useRef<HTMLParagraphElement>(null);
  const { exportingFormat, exportError, downloadExport } = useFirmLogoCopyExport({
    reason,
    website,
    company,
    unresolved,
  });

  const changeFilters = (changes: FilterChanges) => {
    setExpandedRowKey(null);
    updateFilters(changes);
  };

  const selectReason = (value: string | undefined) => {
    if (value === undefined) {
      changeFilters({ reason: undefined, unresolved: undefined });
    } else if (isNeedsCheckingReason(value)) {
      changeFilters({ reason: value, unresolved: "true" });
    } else {
      changeFilters({ reason: value });
    }
  };

  const refreshAfterSave = async (item: FirmLogoFillItem, rowKey: string) => {
    setExpandedRowKey(previous => (previous === rowKey ? null : previous));
    setSavedNotice(
      `Logo saved for ${item.companyName ?? "Unknown firm"}, ${websiteLabel(item.website)}, ${locationLabel(item)}.`,
    );
    setSavedAnnouncement(null);
    setRefreshAfterSaveError(null);
    setPendingSaveRefreshes(previous => previous + 1);

    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: firmLogoKeys.byCompany(item.companyId) }),
        refetch(),
      ]);
    } catch (refreshError) {
      setRefreshAfterSaveError(
        refreshError instanceof Error ? refreshError.message : String(refreshError),
      );
    } finally {
      setPendingSaveRefreshes(previous => previous - 1);
      setFocusAfterSave(logoSetIdOf(item));
    }
  };

  useEffect(() => {
    if (focusAfterSave === null || pendingSaveRefreshes > 0) {
      return;
    }

    const logoSet = document.getElementById(focusAfterSave);

    if (logoSet) {
      setSavedAnnouncement(savedNotice);
      logoSet.focus();
    } else {
      savedNoticeRef.current?.focus();
    }

    setFocusAfterSave(null);
  }, [focusAfterSave, pendingSaveRefreshes, savedNotice]);

  const lastPage = data ? lastPageOf(data.totalItems, data.pageSize) : null;
  const isPastLastPage = lastPage !== null && page > lastPage;

  useEffect(() => {
    if (loading || isRefetching || error || lastPage === null || page <= lastPage) {
      return;
    }

    replacePage(lastPage);
  }, [loading, isRefetching, error, lastPage, page, replacePage]);

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Firm Logo Copy page.</p>
        </div>
      </div>
    );
  }

  const isReloading = isRefetching && pendingSaveRefreshes === 0;
  const isBusy = loading || isReloading || (data === null && !error) || (isPastLastPage && !error);
  const freshData = error || loading || isReloading ? null : data;
  const summary = freshData?.summary ?? null;
  const items = freshData?.items ?? [];
  const hasFilters =
    reason !== undefined ||
    website !== undefined ||
    company !== undefined ||
    unresolved !== undefined;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Firm Logo Copy"
        subtitle={
          canPickLogos
            ? "Check what the one-off copy of existing firm logos will do before it runs. Choosing a logo for a row saves it to the firm straight away."
            : "Check what the one-off copy of existing firm logos will do before it runs. Nothing on this page changes data."
        }
        meta={freshData ? <span>{lastRunText(freshData.lastRun)}</span> : undefined}
      >
        <FirmLogoCopyDownloads
          exportingFormat={exportingFormat}
          onDownload={format => void downloadExport(format)}
        />
      </PageHeader>

      {exportError ? (
        <div className={styles.exportError} role="alert">
          {`Download failed: ${exportError}`}
        </div>
      ) : null}

      <p className={styles.howToFix}>
        To fix a row by hand, open the firm and change its logos in the Logos section.
      </p>

      <div role="status" className="sr-only">
        {savedAnnouncement}
      </div>

      {savedNotice ? (
        <p ref={savedNoticeRef} className={styles.savedNotice} tabIndex={-1}>
          {savedNotice}
        </p>
      ) : null}

      {refreshAfterSaveError ? (
        <div className={styles.exportError} role="alert">
          {`The logo was saved, but the page couldn't be refreshed: ${refreshAfterSaveError}`}
        </div>
      ) : null}

      <FirmLogoCopyFilters
        reason={reason}
        website={website}
        company={company}
        unresolved={unresolved}
        summary={summary}
        onChange={changeFilters}
      />

      {summary ? (
        <OutcomeSummary summary={summary} selectedReason={reason} onSelectReason={selectReason} />
      ) : null}

      <FirmLogoCopyStatus
        isBusy={isBusy}
        isRefetching={isReloading}
        error={error}
        isEmpty={items.length === 0}
        hasFilters={hasFilters}
        onRetry={() => void refetch()}
      />

      {!isBusy && !error && items.length > 0 ? (
        <FirmLogoCopyResults
          items={items}
          page={page}
          pageSize={freshData?.pageSize ?? PAGE_SIZE}
          totalItems={freshData?.totalItems ?? 0}
          expandedRowKey={expandedRowKey}
          canPickLogos={canPickLogos}
          onToggleRow={rowKey =>
            setExpandedRowKey(previous => (previous === rowKey ? null : rowKey))
          }
          onPageChange={nextPage => changeFilters({ page: String(nextPage) })}
          onLogoSaved={refreshAfterSave}
        />
      ) : null}
    </div>
  );
}
